// ChatSessionContext.jsx
// Responsibility: Managing the state of the active chat session.
//
// Chat goes through the PUBLIC API (`aridel-client` → /v1/chat/completions),
// the same surface the SDK uses. Auto-routing happens server-side in one call:
// the chosen model + routing decision arrive inline (top-level `model` +
// `routing` on the first SSE chunk), so there is no separate intake/poll/
// WebSocket phase any more. The public API is stateless, so after each reply
// we persist the message pair via the App API (`app-client.conversations.
// appendMessages`) to keep the conversation-history sidebar in sync.
import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { chat as aridelChat, AridelAuthError } from '../aridel-client';
import { toContentBlocks } from '../attachments';
import { conversations as appConversations, fetchOrCreateApiKey } from '../app-client';
import { useAuth } from './AuthContext';
import { useConversations } from './ConversationsContext';
import { useModelCatalog } from './ModelCatalogContext';

const ChatSessionContext = createContext();

const ROUTE_AI_MODELS = ['Route AI'];

export const ChatSessionProvider = ({ children }) => {
    // --- Core Chat State ---
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
    const [isRouting, setIsRouting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [selectedModel, setSelectedModel] = useState("Route AI");
    const [streamingContent, setStreamingContent] = useState('');
    const [streamingMessageMeta, setStreamingMessageMeta] = useState(null);
    const [viewMode, setViewMode] = useState('chat');

    // Get contexts
    const { userId, setAuthError, setIsSessionValid, refreshUserProfile, userProfile } = useAuth();
    const { conversations, saveConversation, loadConversation: loadConversationData } = useConversations();
    const { slugToName } = useModelCatalog();

    // Credit-funded users must use autorouting — they cannot pin a specific model.
    // BYOK users may pin freely.
    const mustAutoroute = !userProfile?.useOwnKey;

    // Refs: the accumulated stream text + the AbortController for the in-flight
    // completion (so "stop" can cancel the request, which also stops billing).
    const streamingContentRef = useRef('');
    const abortRef = useRef(null);

    const cancelInFlight = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
    }, []);

    // Abort any in-flight stream on unmount.
    useEffect(() => () => cancelInFlight(), [cancelInFlight]);

    // --- Save Current Chat ---
    const saveCurrentChat = useCallback(() => {
        if (conversationId && messages.some(m => m.role === 'user')) {
            const existingConversation = conversations.find(c => c.id === conversationId);

            if (existingConversation) {
                // For existing conversations, only update lastUpdated, preserve the title
                saveConversation({
                    id: conversationId,
                    title: existingConversation.title,
                    lastUpdated: new Date().toISOString(),
                    userId: userId
                });
            } else {
                const firstUserMessage = messages.find(m => m.role === 'user');
                const title = firstUserMessage
                    ? firstUserMessage.content.substring(0, 100)
                    : 'Untitled Chat';

                saveConversation({
                    id: conversationId,
                    title: title,
                    lastUpdated: new Date().toISOString(),
                    userId: userId
                });
            }
        }
    }, [conversationId, messages, conversations, userId, saveConversation]);

    // Build the stateless messages[] for the public API: the visible turn
    // history (status/system lines excluded) plus the new user turn. History
    // is text-only — attachments ride on the turn they were sent with; resending
    // their base64 payloads every turn would blow the request-size limits.
    const buildApiMessages = (priorMessages, newUserContent) => {
        const history = priorMessages
            .filter(m => m.role === 'user' || m.role === 'assistant')
            .map(m => ({ role: m.role, content: m.content }));
        history.push({ role: 'user', content: newUserContent });
        return history;
    };

    // --- Submit Message (file handling lives in FileUploadContext) ---
    // `attachments` are EncodedAttachment objects (see src/attachments.ts) —
    // already base64 data URIs, sent inline on the public-API request exactly
    // as an SDK consumer would send them.
    const submitMessage = async (prompt, attachments = [], webSearchEnabled = false) => {
        setIsSubmitting(true);
        console.log(`ChatSessionContext: Submitting message for user ${userId}`);

        const convId = conversationId || crypto.randomUUID();
        const isNewConv = !conversations.find(c => c.id === convId);
        if (!conversationId) setConversationId(convId);

        const userMessage = {
            role: 'user',
            content: prompt,
            files: attachments.map(a => ({ name: a.name, size: a.size })),
            timestamp: new Date().toISOString(),
        };

        // The new user turn: plain text, or content blocks when files are
        // attached (the shape shared/attachments.py parses server-side).
        const newUserContent = attachments.length
            ? [
                ...(prompt ? [{ type: 'text', text: prompt }] : []),
                ...toContentBlocks(attachments),
              ]
            : prompt;

        // Snapshot the history BEFORE appending the new user message to state.
        const apiMessages = buildApiMessages(messages, newUserContent);

        const isRouteAI = ROUTE_AI_MODELS.includes(selectedModel);
        const routedNow = isRouteAI || mustAutoroute;
        const model = routedNow ? 'auto' : selectedModel;

        setMessages(prev => [...prev, userMessage]);
        setIsWaitingForResponse(true);
        setIsRouting(routedNow);
        setInputValue('');
        setViewMode('chat');

        const req = { model, messages: apiMessages };
        // Document blocks require the file-parser plugin server-side
        // (images pass through the vision gate without one).
        if (attachments.some(a => a.kind === 'file')) {
            req.plugins = [{ id: 'file-parser' }];
        }
        // Web search is native-only: the serving provider's own search
        // mechanism runs it (models without one serve without search).
        if (webSearchEnabled) {
            req.plugins = [...(req.plugins || []), { id: 'web' }];
        }

        const abort = new AbortController();
        abortRef.current = abort;

        streamingContentRef.current = '';
        setStreamingContent('');
        setStreamingMessageMeta({ role: 'assistant', isStreaming: true });

        // Captured as the stream unfolds.
        let chosenModel = routedNow ? null : model;
        let routingObj = null;
        let usage = null;

        const runStream = async () => {
            for await (const chunk of aridelChat.completions.stream(req, { signal: abort.signal })) {
                const choice = chunk.choices?.[0] || {};
                // The first chunk surfaces the chosen model + routing decision.
                // The API echoes the harness-safe slug; render the display name.
                if (chunk.model) chosenModel = slugToName?.[chunk.model] ?? chunk.model;
                if (chunk.routing) routingObj = chunk.routing;
                if (chunk.usage) usage = chunk.usage;
                if (chosenModel) {
                    setIsRouting(false);
                    setStreamingMessageMeta(meta => ({ ...(meta || {}), role: 'assistant', modelUsed: chosenModel, isStreaming: true }));
                }
                const delta = choice.delta?.content;
                if (delta) {
                    streamingContentRef.current += delta;
                    setStreamingContent(prev => prev + delta);
                }
            }
        };

        try {
            try {
                await runStream();
            } catch (err) {
                // First call after sign-in may race the key handshake — mint it
                // once and retry, then give up if it still fails.
                if (err instanceof AridelAuthError) {
                    await fetchOrCreateApiKey();
                    await runStream();
                } else {
                    throw err;
                }
            }

            const finalContent = streamingContentRef.current;
            if (!finalContent.trim()) {
                console.log('ChatSessionContext: Empty completion, cleaning up');
                setIsWaitingForResponse(false);
                setIsRouting(false);
                setIsSubmitting(false);
                setStreamingContent('');
                setStreamingMessageMeta(null);
                return;
            }

            const assistantMessage = {
                role: 'assistant',
                content: finalContent,
                modelUsed: chosenModel || model,
                isStreaming: false,
                webSearchEnabled: webSearchEnabled,
                ...(routingObj ? { routing: routingObj } : {}),
            };
            setMessages(prev => [...prev, assistantMessage]);

            setStreamingContent('');
            setStreamingMessageMeta(null);
            setIsWaitingForResponse(false);
            setIsRouting(false);
            setIsSubmitting(false);
            console.log('ChatSessionContext: Completion finished successfully');

            // Persist the pair server-side (the public API is stateless).
            // Best-effort: a failed write must not lose the on-screen reply.
            appConversations.appendMessages(convId, {
                user_message: { content: prompt, files: userMessage.files },
                assistant_message: {
                    content: finalContent,
                    model_used: chosenModel || model,
                    ...(routingObj ? { routing_details: routingObj } : {}),
                    ...(usage ? { token_usage: usage } : {}),
                    web_search_enabled: webSearchEnabled,
                },
            }).then(() => {
                saveConversation({
                    id: convId,
                    ...(isNewConv ? { title: prompt.substring(0, 100) || 'Untitled Chat' } : {}),
                    lastUpdated: new Date().toISOString(),
                    userId: userId,
                });
            }).catch(err => {
                console.error('ChatSessionContext: Failed to persist conversation:', err);
            });

            refreshUserProfile().catch(err => {
                console.error('Failed to refresh profile after message:', err);
            });
        } catch (err) {
            // User-initiated abort is handled in stopGenerating; don't double-report.
            if (abort.signal.aborted) return;

            console.error(`ChatSessionContext: Chat completion failed for user ${userId}:`, err);
            if (err instanceof AridelAuthError || err?.status === 401 || err?.status === 403) {
                setAuthError('Authentication failed. Please sign in again.');
                setIsSessionValid(false);
            }

            const partial = streamingContentRef.current;
            setMessages(prev => [...prev, partial
                ? {
                    role: 'assistant',
                    content: partial + '\n\n*[Error: response interrupted]*',
                    modelUsed: chosenModel || model,
                    isStreaming: false,
                }
                : { role: 'system', content: err?.message || 'An error occurred. Please try again.' }
            ]);

            setStreamingContent('');
            setStreamingMessageMeta(null);
            setIsWaitingForResponse(false);
            setIsRouting(false);
            setIsSubmitting(false);
        } finally {
            abortRef.current = null;
        }
    };

    // --- Start New Chat ---
    const startNewChat = () => {
        console.log(`ChatSessionContext: User ${userId} starting new chat`);

        cancelInFlight();
        saveCurrentChat();

        setMessages([]);
        setConversationId(null);
        setIsWaitingForResponse(false);
        setIsRouting(false);
        setInputValue('');
        setViewMode('chat');
        setStreamingContent('');
        setStreamingMessageMeta(null);
    };

    // --- Load Conversation ---
    const loadConversationInChat = async (convId) => {
        if (isWaitingForResponse) {
            console.log('ChatSessionContext: Cannot load conversation while waiting for response');
            return;
        }

        console.log(`ChatSessionContext: Loading conversation ${convId}`);

        cancelInFlight();
        saveCurrentChat();

        setStreamingContent('');
        setStreamingMessageMeta(null);

        try {
            setMessages([{ role: 'system', content: 'Loading conversation...' }]);

            const { conversationId: loadedConvId, messages: loadedMessages } = await loadConversationData(convId);

            setMessages(loadedMessages);
            setConversationId(loadedConvId);
            setViewMode('chat');

            console.log(`ChatSessionContext: Successfully loaded conversation ${convId}`);
        } catch (error) {
            console.error(`ChatSessionContext: Failed to load conversation ${convId}:`, error);
            setMessages([{ role: 'system', content: `Error: ${error.message}` }]);
            setConversationId(null);
        }
    };

    // --- Stop Generating ---
    const stopGenerating = () => {
        console.log('ChatSessionContext: Stopping generation');

        cancelInFlight();
        setIsRouting(false);
        setIsWaitingForResponse(false);
        setIsSubmitting(false);

        if (streamingContentRef.current) {
            const stoppedMessage = {
                role: 'assistant',
                content: streamingContentRef.current + '\n\n*[Generation stopped by user]*',
                modelUsed: streamingMessageMeta?.modelUsed || selectedModel,
                isStreaming: false,
            };
            setMessages(prev => [...prev, stoppedMessage]);
        }

        streamingContentRef.current = '';
        setStreamingContent('');
        setStreamingMessageMeta(null);
    };

    const value = {
        // State
        messages,
        inputValue,
        setInputValue,
        isWaitingForResponse,
        isRouting,
        isSubmitting,
        conversationId,
        selectedModel,
        setSelectedModel,
        mustAutoroute,
        streamingContent,
        streamingMessageMeta,
        viewMode,
        setViewMode,

        // Actions
        startNewChat,
        loadConversationInChat,
        stopGenerating,
        submitMessage,
        saveCurrentChat
    };

    return (
        <ChatSessionContext.Provider value={value}>
            {children}
        </ChatSessionContext.Provider>
    );
};

export const useChatSession = () => {
    const context = useContext(ChatSessionContext);
    if (context === undefined) {
        throw new Error('useChatSession must be used within a ChatSessionProvider');
    }
    return context;
};
